// src/pages/layout.js
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaSignOutAlt,
  FaCoins,
  FaImages,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { BiCarousel, BiImageAlt } from "react-icons/bi";
import { RiHome2Line, RiHome2Fill, RiFileHistoryLine, RiFileHistoryFill, RiNotification2Line } from "react-icons/ri";
import { HiOutlineDocumentChartBar, HiDocumentChartBar, HiOutlineDocumentMagnifyingGlass, HiDocumentMagnifyingGlass, HiOutlineClipboardDocument, HiClipboardDocument, HiOutlineDocumentDuplicate, HiDocumentDuplicate } from "react-icons/hi2";
import { MdOutlineHelpCenter, MdHelpCenter, MdOutlineContactSupport, MdContactSupport, MdAdminPanelSettings, MdOutlineAdminPanelSettings } from "react-icons/md";
import { GrDocumentVideo, GrDocumentPpt, GrDocumentImage, GrDocumentSound } from "react-icons/gr";
import { CgProfile } from "react-icons/cg";
import { LuTickets } from "react-icons/lu";

import logo from "../../assets/images/newlogo.png";
import { useNotifications } from "../../contexts/NotificationContext";
import { useWebSockets } from "../../contexts/WebSocketContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function SidebarTopbar({ children }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [availableCredits, setAvailableCredits] = useState(null);
  const [totalCredits, setTotalCredits] = useState(null);
  const [creditsUsed, setCreditsUsed] = useState(null);
  const [activeNotificationTab, setActiveNotificationTab] = useState("unread");
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());

  // Use the NotificationContext
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markNotificationAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Use the WebSocket context for logout
  const { disconnectGlobalWebSocket } = useWebSockets();

  const notificationDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Toggle expand/collapse for a notification
  const toggleExpandNotification = (e, notificationId) => {
    e.stopPropagation();
    setExpandedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Get notification icon based on type
  const getNotificationIcon = (notificationType) => {
    // Convert to lowercase and trim
    const type = String(notificationType || '').toLowerCase().trim();

    // Log for debugging
    console.log('[Notification Icon] Raw type:', notificationType, '| Processed:', type);

    if (type === 'video') {
      return <GrDocumentVideo color="#1e4de3" size={16} />;
    }
    if (type === 'ppt' || type === 'presentation' || type === 'ppt_content') {
      return <GrDocumentPpt color="#1e4de3" size={16} />;
    }
    if (type === 'poster') {
      return <GrDocumentImage color="#1e4de3" size={16} />;
    }
    if (type === 'audio') {
      return <GrDocumentSound color="#1e4de3" size={16} />;
    }
    if (type === 'carousel') {
      return <BiCarousel color="#1e4de3" size={16} />;
    }
    if (type === 'instapost' || type === 'post') {
      return <BiImageAlt color="#1e4de3" size={16} />;
    }
    if (type === 'images' || type === 'figures' || type === 'graphimages' || type === 'image') {
      return <FaImages color="#1e4de3" size={16} />;
    }

    // Default fallback
    console.warn('[Notification Icon] Unknown type:', notificationType);
    return <RiNotification2Line color="#1e4de3" size={16} />;
  };

  // Get time period for grouping notifications
  const getTimePeriod = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastMonthStart = new Date(today);
    lastMonthStart.setDate(lastMonthStart.getDate() - 30);

    if (date >= today) return 'Today';
    if (date >= yesterday) return 'Yesterday';
    if (date >= lastWeekStart) return 'Last Week';
    if (date >= lastMonthStart) return 'Last Month';
    return 'Older';
  };

  // Group notifications by time period
  const groupNotificationsByTime = (notificationsList) => {
    const groups = {
      'Today': [],
      'Yesterday': [],
      'Last Week': [],
      'Last Month': [],
      'Older': []
    };

    notificationsList.forEach(notification => {
      const period = getTimePeriod(notification.created_at || notification.timestamp);
      groups[period].push(notification);
    });

    return groups;
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markNotificationAsRead(notification.id);
    }
    setNotificationsOpen(false);

    if (notification.publication_id) {
      navigate(`/publicationview/${notification.publication_id}`);
    }
  };



  const pathToId = {
    "/dashboard": "dashboard",
    "/GenerationsHistory": "generations",
    "/AccountSettings": "account",
    "/uploadfile": "pubvision",
    "/pubchat-upload": "pubchat",
    "/helpSection": "help",
    "/pubVerse": "pubverse",
    "/pubLibrary": "publibrary",
    "/support-tickets": "support",
    "/admin-panel": "admin-panel",
  };

  // Determine active item based on pathname (supports dynamic routes)
  const getActiveItem = () => {
    const path = location.pathname;

    // Check exact matches first
    if (pathToId[path]) {
      return pathToId[path];
    }

    // Handle dynamic routes
    if (path.startsWith("/publicationView/") || path.startsWith("/publicationview/")) {
      return "pubvision";
    }
    if (path.startsWith("/pubChat/") || path.startsWith("/pubchat/")) {
      return "pubchat";
    }

    // Default to dashboard
    return "dashboard";
  };

  const activeItem = getActiveItem();

  // Get organization data from localStorage
  const organizationData = JSON.parse(localStorage.getItem("organizationData") || "{}");
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const userRole = userData.Role || userData.role || "";
  const hideAccountSettings = organizationData.id === 1;

  const sidebarItems = [
    { id: "dashboard", label: "Dashboard", iconOutline: <RiHome2Line />, iconFilled: <RiHome2Fill /> },
    { id: "pubvision", label: "PubGen", iconOutline: <HiOutlineDocumentChartBar />, iconFilled: <HiDocumentChartBar /> },
    { id: "pubchat", label: "PubChat", iconOutline: <HiOutlineDocumentMagnifyingGlass />, iconFilled: <HiDocumentMagnifyingGlass /> },
    { id: "publibrary", label: "PubLibrary", iconOutline: <HiOutlineClipboardDocument />, iconFilled: <HiClipboardDocument /> },
    { id: "pubverse", label: "PubVerse", iconOutline: <HiOutlineDocumentDuplicate />, iconFilled: <HiDocumentDuplicate /> },
    { id: "generations", label: "History", iconOutline: <RiFileHistoryLine />, iconFilled: <RiFileHistoryFill /> },
    { id: "account", label: "Account Settings", iconOutline: <CgProfile />, iconFilled: <CgProfile /> },
    { id: "help", label: "Help", iconOutline: <MdOutlineHelpCenter />, iconFilled: <MdHelpCenter /> },
    { id: "support", label: "Support Tickets", iconOutline: <LuTickets />, iconFilled: <LuTickets /> },
    ...((userRole.toLowerCase() === "admin" || userRole.toLowerCase() === "owner")
      ? [{ id: "admin-panel", label: "Admin Panel", iconOutline: <MdOutlineAdminPanelSettings />, iconFilled: <MdAdminPanelSettings /> }]
      : [])
  ].filter(item => {
    if (item.id === "account" && hideAccountSettings) return false;
    if (item.id === "pubverse" && userRole.toLowerCase() === "admin") return false;
    return true;
  });

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    fetch(`${BASE_URL}/getaccountsettings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch user info");
        return res.json();
      })
      .then(data => {
        if (data.firstName) setFirstName(data.firstName);
      })
      .catch(() => {
        console.error("Error fetching user info");
      });
  }, []);

  // NOTE: fetchNotifications is intentionally NOT called here.
  // NotificationContext calls it once on mount — calling it here too
  // causes one extra call per SidebarTopbar instance (every page, VideoEditorView, SlideEditorView, etc.)

  // Fetch available credits
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    fetch(`${BASE_URL}/credits_usage`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch credits");
        return res.json();
      })
      .then(data => {
        if (data.available_credits !== undefined) {
          setAvailableCredits(data.available_credits);
        }
        if (data.total_credits !== undefined) {
          setTotalCredits(data.total_credits);
        }
        if (data.credits_used !== undefined) {
          setCreditsUsed(data.credits_used);
        }
      })
      .catch(() => {
        console.error("Error fetching credits");
      });
  }, []);

  useEffect(() => {

    const handleClickOutside = (event) => {

      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen]);

  // Click outside handler for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileOpen]);

  const handleItemClick = id => {
    switch (id) {
      case "dashboard":
        navigate("/dashboard");
        break;
      case "pubvision":
        navigate("/uploadfile", { state: { target: "publicationView" } });
        break;
      case "pubchat":
        navigate("/pubchat-upload");
        break;
      case "pubverse":
        navigate("/pubVerse");
        break;
      case "publibrary":
        navigate("/pubLibrary");
        break;
      case "generations":
        navigate("/GenerationsHistory");
        break;
      case "account":
        navigate("/AccountSettings");
        break;
      case "help":
        navigate("/helpSection");
        break;
      case "support":
        navigate("/support-tickets");
        break;
      case "admin-panel":
        navigate("/admin-panel");
        break;
      default:
    }
  };

  const handleLogout = () => {
    // Disconnect global WebSocket before logging out
    disconnectGlobalWebSocket();

    // Clear all auth data
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("organizationData");

    // Navigate to login page
    navigate("/");
  };
  const handleAccNavigate = () => {
    navigate("/AccountSettings");
  };

  const toggleProfile = () => {
    setProfileOpen(open => !open);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <style>{`
        html, body {
          margin: 0; padding: 0;
        }
        .topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #ffffff;
          height: 60px;
          padding: 0 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          position: relative;
          z-index: 1000;
        }
        .topbar-left img {
          height: 40px;
        }
        .topbar-right {
          display: flex;
          align-items: center;
          position: relative;
        }
        .icon-btn {
          margin-left: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        .icon-btn:hover {
          opacity: 1;
        }
        .icon-btn > svg:hover {
          opacity: 0.7;
        }
        .profile-name {
          margin-left: 8px;
          font-size: 16px;
          color: #000000;
          cursor: pointer;
        }
        .profile-dropdown {
          position: absolute;
          top: 45px;
          right: 0;
          background-color: #ffffff;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          border-radius: 8px;
          overflow: hidden;
          z-index: 9999;
          border: 1px solid #e0e0e0;
          min-width: 200px;
        }
        .profile-dropdown-item {
          padding: 10px 20px;
          font-size: 14px;
          color: #000000;
          cursor: pointer;
          white-space: nowrap;
          background-color: #ffffff;
        }
        .profile-dropdown-item:hover {
          background-color: #e2e9ff;
        }
        .sidebar {
          background-color: #1e4de3;
          width: 250px;
          display: flex;
          flex-direction: column;
          padding-top: 20px;
          overflow-y: auto;
          box-sizing: border-box;
          height: calc(100vh - 60px);
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        .sidebar::-webkit-scrollbar {
          display: none; /* Chrome, Safari and Opera */
        }
        .sidebar-item {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          font-size: 16px;
          font-weight: 500;
          color: #ffffff;
          cursor: pointer;
          margin-bottom: 5px;
          margin-left: 12px;
        }
        .sidebar-item:hover {
          background-color: rgba(255, 255, 255, 0.25);
          color: #ffffff;
          border-top-left-radius: 30px;
          border-bottom-left-radius: 30px;
        }
        .sidebar-item:hover .sidebar-item-icon {
          color: #ffffff;
        }
        .sidebar-item-icon {
          margin-right: 10px;
          background-color: transparent;
          color: #ffffff;
          padding: 8px;
          border-radius: 10%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sidebar-item.active {
          background-color: #ffffff;
          color: #1e4de3;
          border-top-left-radius: 30px;
          border-bottom-left-radius: 30px;
        }
        .sidebar-item.active .sidebar-item-icon {
          color: #1e4de3;
        }
        .content {
          flex: 1;
          padding: 32px;
          background-color: #f8f8f8;
          overflow-y: auto;
          box-sizing: border-box;
          height: calc(100vh - 60px);
        }
        .notification-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background-color: #ff0000;
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 10px;
        }
         .notification-dropdown {
          position: absolute;
          top: 60px;
          right: 60px;
          background-color: #ffffff;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          border-radius: 8px;
          width: 300px;
          max-height: 400px;
          overflow-y: auto;
          z-index: 1000;
        }
        
        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          border-bottom: 1px solid #eee;
        }

        .notification-item {
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
        }
        
        .notification-item.unread {
          background-color: #f0f7ff;
        }
        
        .notification-message {
          font-size: 14px;
          margin-bottom: 5px;
        }
        
        .notification-time {
          font-size: 11px;
          color: #888;
      `}</style>

      <div className="topbar">
        <div className="topbar-left">
          <img src={logo} alt="Pubvision AI Logo" />
        </div>
        <div className="topbar-right" style={{ display: 'flex', alignItems: 'center' }}>
          {/* 🔔 Notification Icon */}
          <div className="icon-btn" onClick={() => setNotificationsOpen(!notificationsOpen)} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <RiNotification2Line size={20} color="#9ca3af" />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#1e4de3',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '10px'
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </div>

          {/* Notifications Dropdown */}
          {notificationsOpen && (
            <div
              ref={notificationDropdownRef}
              style={{
                position: 'absolute',
                top: '60px',
                right: '60px',
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: '12px',
                width: '380px',
                maxHeight: '500px',
                overflowY: 'auto',
                zIndex: 1000,
                border: '1px solid #e0e0e0'
              }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '1px solid #eee'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>Notifications</h3>
                <button
                  onClick={markAllAsRead}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1e4de3',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  Mark all as read
                </button>
              </div>

              {/* Tabs */}
              <div style={{
                display: 'flex',
                borderBottom: '1px solid #eee',
                padding: '0 20px'
              }}>
                <button
                  onClick={() => setActiveNotificationTab('unread')}
                  style={{
                    padding: '12px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeNotificationTab === 'unread' ? '2px solid #1e4de3' : '2px solid transparent',
                    color: activeNotificationTab === 'unread' ? '#1e4de3' : '#666',
                    fontWeight: activeNotificationTab === 'unread' ? '600' : '400',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Unread {unreadCount > 0 && `(${unreadCount})`}
                </button>
                <button
                  onClick={() => setActiveNotificationTab('all')}
                  style={{
                    padding: '12px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeNotificationTab === 'all' ? '2px solid #1e4de3' : '2px solid transparent',
                    color: activeNotificationTab === 'all' ? '#1e4de3' : '#666',
                    fontWeight: activeNotificationTab === 'all' ? '600' : '400',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  All
                </button>
              </div>

              {/* Notification List */}
              <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {(() => {
                  const filteredNotifications = activeNotificationTab === 'unread'
                    ? notifications.filter(n => !n.is_read)
                    : notifications;

                  if (filteredNotifications.length === 0) {
                    return (
                      <div style={{
                        padding: '40px 20px',
                        textAlign: 'center',
                        color: '#888'
                      }}>
                        <RiNotification2Line size={32} color="#ddd" style={{ marginBottom: '12px' }} />
                        <p style={{ margin: 0, fontSize: '14px' }}>
                          {activeNotificationTab === 'unread' ? 'No unread notifications' : 'No notifications'}
                        </p>
                      </div>
                    );
                  }

                  const groupedNotifications = groupNotificationsByTime(filteredNotifications);
                  const timePeriodOrder = ['Today', 'Yesterday', 'Last Week', 'Last Month', 'Older'];

                  return timePeriodOrder.map(period => {
                    const periodNotifications = groupedNotifications[period];
                    if (!periodNotifications || periodNotifications.length === 0) return null;

                    return (
                      <div key={period}>
                        {/* Time Period Header */}
                        <div style={{
                          padding: '10px 20px',
                          backgroundColor: '#f5f5f5',
                          borderBottom: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#666',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {period}
                          </span>
                        </div>

                        {/* Notifications in this period */}
                        {periodNotifications.map(notification => {
                          // Debug log to check notification object
                          console.log('[Notification Object]', JSON.stringify(notification, null, 2));

                          const isExpanded = expandedNotifications.has(notification.id);
                          const description = notification.description || notification.message || '';
                          const shouldTruncate = description.length > 80;

                          return (
                            <div
                              key={notification.id}
                              style={{
                                padding: '14px 20px',
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: notification.is_read ? '#fff' : '#f0f7ff',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = notification.is_read ? '#f8f8f8' : '#e8f2ff'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = notification.is_read ? '#fff' : '#f0f7ff'}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                {/* Icon */}
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: '#e8f0fe',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  {getNotificationIcon(notification.type)}
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {/* Title row with expand button */}
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px'
                                  }}>
                                    <div
                                      onClick={() => handleNotificationClick(notification)}
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: notification.is_read ? '500' : '600',
                                        color: '#333',
                                        lineHeight: '1.4',
                                        flex: 1,
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                      title={notification.title || 'Publication'}
                                    >
                                      {notification.title || 'Publication'}
                                    </div>
                                    {shouldTruncate && (
                                      <button
                                        onClick={(e) => toggleExpandNotification(e, notification.id)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          padding: '2px',
                                          color: '#1e4de3',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0
                                        }}
                                        title={isExpanded ? 'Collapse' : 'Expand'}
                                      >
                                        {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                      </button>
                                    )}
                                  </div>

                                  {/* Description */}
                                  <div
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{
                                      fontSize: '12px',
                                      color: '#555',
                                      lineHeight: '1.5',
                                      cursor: 'pointer',
                                      overflow: isExpanded ? 'visible' : 'hidden',
                                      textOverflow: isExpanded ? 'unset' : 'ellipsis',
                                      display: isExpanded ? 'block' : '-webkit-box',
                                      WebkitLineClamp: isExpanded ? 'unset' : 2,
                                      WebkitBoxOrient: 'vertical',
                                      maxHeight: isExpanded ? 'none' : '36px',
                                      marginBottom: '4px'
                                    }}
                                  >
                                    {description}
                                  </div>

                                  {/* Date */}
                                  <div style={{
                                    fontSize: '11px',
                                    color: '#888',
                                    cursor: 'pointer'
                                  }}
                                    onClick={() => handleNotificationClick(notification)}>
                                    {notification.created_at && (() => {
                                      const createdDate = new Date(notification.created_at);
                                      const now = new Date();
                                      const diffMs = now - createdDate;
                                      const diffMins = Math.floor(diffMs / (1000 * 60));
                                      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                                      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                                      // If it's today, show relative time
                                      if (createdDate >= today) {
                                        if (diffMins < 1) {
                                          return 'Just now';
                                        } else if (diffMins < 60) {
                                          return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
                                        } else {
                                          return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
                                        }
                                      }

                                      // Otherwise, show date
                                      return createdDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      });
                                    })()}
                                  </div>
                                </div>

                                {/* Delete button - only show in All tab */}
                                {/* {activeNotificationTab === 'all' && (
                                  <button
                                    onClick={(e) => handleDeleteNotification(e, notification.id)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '4px',
                                      color: '#999',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '4px',
                                      transition: 'all 0.2s',
                                      flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = '#ff4444';
                                      e.currentTarget.style.backgroundColor = '#fff0f0';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = '#999';
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                    title="Delete notification"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                )} */}

                                {/* Unread indicator */}
                                {!notification.is_read && (
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#1e4de3',
                                    flexShrink: 0,
                                    marginTop: '4px'
                                  }} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* 👤 Profile Section */}
          <div className="icon-btn" onClick={toggleProfile} ref={profileDropdownRef}>
            <CgProfile size={20} color="#9ca3af" />
            <span className="profile-name">{firstName || "Loading…"}</span>
            {profileOpen && (
              <div className="profile-dropdown">
                {/* Credits Display with Progress Bar */}
                <div
                  className="profile-dropdown-item"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    cursor: "default",
                    borderBottom: "1px solid #eee",
                    padding: "12px 20px"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FaCoins style={{ marginRight: "8px", color: "#1e4de3" }} />
                    <span style={{ fontSize: "13px", fontWeight: "500" }}>Credits</span>
                  </div>
                  {/* Progress Bar */}
                  <div style={{
                    width: "100%",
                    height: "8px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    overflow: "hidden"
                  }}>
                    <div style={{
                      width: totalCredits !== null && availableCredits !== null
                        ? `${Math.min((availableCredits / totalCredits) * 100, 100)}%`
                        : "0%",
                      height: "100%",
                      backgroundColor: "#1e4de3",
                      transition: "width 0.3s ease",
                      borderRadius: "4px"
                    }} />
                  </div>
                  <div style={{
                    fontSize: "12px",
                    color: "#666",
                    textAlign: "left"
                  }}>
                    {availableCredits !== null && totalCredits !== null
                      ? `${availableCredits} / ${totalCredits} credits left`
                      : "..."
                    }
                  </div>
                </div>
                {/* <div
                  className="profile-dropdown-item"
                  onClick={handleAccNavigate}
                >
                  Edit Profile
                </div> */}
                <div
                  className="profile-dropdown-item"
                  onClick={handleLogout}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#1e4de3"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#000000"}
                >
                  <FaSignOutAlt style={{ marginRight: "8px", color: "#1e4de3" }} />
                  Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1 }}>
        <div className="sidebar">
          {sidebarItems.map(item => (
            <div
              key={item.id}
              className={`sidebar-item ${activeItem === item.id ? "active" : ""
                }`}
              onClick={() => handleItemClick(item.id)}
            >
              <div className="sidebar-item-icon">
                {React.cloneElement(
                  activeItem === item.id ? item.iconFilled : item.iconOutline,
                  { size: 20 }
                )}
              </div>
              {item.label}
            </div>
          ))}
        </div>

        <div className="content">{children}</div>
      </div>
    </div>
  );
}

SidebarTopbar.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SidebarTopbar;